import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import blogPosts from "@/data/blogPosts";
import { trackAbEvent } from "@/lib/analytics";


interface RelatedPostsProps {
  currentLink: string;
  /** Optional explicit list of internal links to feature. Takes precedence over topic/category matching. */
  links?: string[];
  /**
   * Optional list of topic keywords/phrases (case-insensitive). Posts are scored
   * by how many topics appear in their title, excerpt, category, or highlights.
   * Highest-scoring internal posts are surfaced first. Falls back to category /
   * DEFAULT_RELATED when no topic matches exist.
   */
  topics?: string[];
  /** Category to match for related posts. Falls back to most recent posts if not enough matches. */
  category?: string;
  heading?: string;
  eyebrow?: string;
  limit?: number;
}

const DEFAULT_RELATED = [
  "/blog/personal-training-for-beginners-woodstock",
  "/blog/functional-strength-training-after-40",
  "/blog/semi-private-personal-training-woodstock",
  "/blog/semi-private-vs-traditional-gyms",
  "/blog/scale-menopause-weight-loss-woodstock",
  "/blog/functional-strength-training-after-40",
];

type ScorablePost = (typeof blogPosts)[number];

const postHaystack = (p: ScorablePost): string =>
  [p.title, p.excerpt, p.category, ...(p.highlights ?? [])]
    .filter(Boolean)
    .join(" \n ")
    .toLowerCase();

const scorePost = (p: ScorablePost, topics: string[]): number => {
  const hay = postHaystack(p);
  let score = 0;
  for (const raw of topics) {
    const t = raw.trim().toLowerCase();
    if (!t) continue;
    // Count occurrences so highly-relevant posts outrank single-mentions.
    let idx = hay.indexOf(t);
    while (idx !== -1) {
      score += 1;
      idx = hay.indexOf(t, idx + t.length);
    }
  }
  return score;
};

const RelatedPosts = ({
  currentLink,
  links,
  topics,
  category,
  heading = "Related Articles",
  eyebrow = "Read Next",
  limit = 3,
}: RelatedPostsProps) => {
  let posts: typeof blogPosts = [];

  if (links && links.length > 0) {
    const candidateLinks = links.filter((l) => l !== currentLink);
    posts = candidateLinks
      .map((l) => blogPosts.find((p) => p.link === l && p.isInternal))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .slice(0, limit);
  } else {
    const internal = blogPosts.filter((p) => p.isInternal && p.link !== currentLink);

    // 1. Topic-based scoring (preferred when topics are provided).
    const byTopic = topics && topics.length > 0
      ? internal
          .map((p) => ({ p, score: scorePost(p, topics) }))
          .filter((x) => x.score > 0)
          .sort((a, b) => b.score - a.score)
          .map((x) => x.p)
      : [];

    // 2. Category match as secondary signal.
    const byCategory = category
      ? internal.filter((p) => p.category?.toLowerCase() === category.toLowerCase())
      : [];

    // 3. Recent default list as a stable fallback ordering for SSR consistency.
    const recencyFallback = DEFAULT_RELATED
      .filter((l) => l !== currentLink)
      .map((l) => blogPosts.find((p) => p.link === l && p.isInternal))
      .filter((p): p is NonNullable<typeof p> => Boolean(p));

    const seenTopic = new Set(byTopic.map((p) => p.link));
    const fillers = internal.filter((p) => !seenTopic.has(p.link));
    const merged = [...byTopic, ...byCategory, ...recencyFallback, ...fillers];

    const dedup: typeof blogPosts = [];
    const seen2 = new Set<string>();
    for (const p of merged) {
      if (seen2.has(p.link)) continue;
      seen2.add(p.link);
      dedup.push(p);
      if (dedup.length >= limit) break;
    }
    posts = dedup;
  }



  if (posts.length === 0) return null;

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://hivefitnessjourney.com";

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: heading,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: posts.length,
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${origin}${post.link}`,
      name: post.title,
      image: post.image,
    })),
  };

  return (
    <section className="py-16 section-padding border-t border-border/50">
      <script
        type="application/ld+json"
        // JSON.stringify escapes </ safely for schema.org payloads.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }}
      />
      <div className="max-w-6xl mx-auto">
        <div className="mb-10 text-center">
          <span className="block text-primary font-semibold text-sm uppercase tracking-widest mb-3">
            {eyebrow}
          </span>
          <h2 className="font-display text-3xl sm:text-5xl text-gradient-honey">{heading}</h2>
        </div>


        <div className="grid gap-6 md:grid-cols-3">
          {posts.map((post, index) => (
            <Link
              key={post.link}
              to={post.link}
              onClick={() =>
                trackAbEvent("read_next", "default", "conversion", {
                  cta_label: post.title,
                  meta: {
                    destination: post.link,
                    position: index + 1,
                    from_path:
                      typeof window !== "undefined" ? window.location.pathname : "",
                  },
                })
              }
              className="group flex flex-col rounded-2xl overflow-hidden border border-border bg-card/40 hover:border-primary/60 transition-colors"
            >

              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={post.image}
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex flex-col flex-1 p-5">
                <span className="text-primary text-xs font-semibold uppercase tracking-widest">
                  {post.category}
                </span>
                <h3 className="font-display text-xl mt-2 leading-tight text-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-3 line-clamp-3">{post.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-primary text-sm font-semibold">
                  Read post <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedPosts;
